import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Package, ChefHat, FileText, ArrowRight, Star, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useCurrency } from '../contexts/CurrencyContext';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { stripHtml } from '../utils/stripHtml';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SearchResults = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [searchInput, setSearchInput] = useState(query);
  const [results, setResults] = useState({ products: [], recipes: [], blogs: [] });
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const { formatPrice } = useCurrency();

  useEffect(() => {
    setSearchInput(query);
    if (query) {
      performSearch(query);
    }
  }, [query]);

  const performSearch = async (searchQuery) => {
    setLoading(true);
    try {
      const [productsRes, recipesRes, blogsRes] = await Promise.all([
        axios.get(`${API_URL}/api/products`),
        axios.get(`${API_URL}/api/recipes`),
        axios.get(`${API_URL}/api/blog/posts`)
      ]);

      const queryLower = searchQuery.toLowerCase();

      // Filter products
      const products = productsRes.data.filter(p => 
        p.name.toLowerCase().includes(queryLower) ||
        p.description.toLowerCase().includes(queryLower) ||
        p.category.toLowerCase().includes(queryLower)
      );

      // Filter recipes
      const recipes = recipesRes.data.filter(r =>
        r.title.toLowerCase().includes(queryLower) ||
        (r.description && r.description.toLowerCase().includes(queryLower)) ||
        (r.category && r.category.toLowerCase().includes(queryLower))
      );

      // Filter blogs
      const blogs = blogsRes.data.filter(b =>
        b.title.toLowerCase().includes(queryLower) ||
        (b.excerpt && b.excerpt.toLowerCase().includes(queryLower)) ||
        (b.content && b.content.toLowerCase().includes(queryLower))
      );

      setResults({ products, recipes, blogs });
    } catch (e) {
      console.error('Search error:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchInput.trim()) {
      setSearchParams({ q: searchInput.trim() });
    }
  };

  const totalResults = results.products.length + results.recipes.length + results.blogs.length;

  const tabs = [
    { id: 'all', label: `All (${totalResults})` },
    { id: 'products', label: `Products (${results.products.length})`, icon: Package },
    { id: 'recipes', label: `Recipes (${results.recipes.length})`, icon: ChefHat },
    { id: 'blog', label: `Blog (${results.blogs.length})`, icon: FileText }
  ];

  return (
    <div className="min-h-screen bg-[#f0f4f8]">
      <Navbar hideSearchBar />

      <div className="pt-32 pb-16 section-padding">
        <div className="max-w-6xl mx-auto">
          {/* Search Header */}
          <div className="bg-white rounded-2xl p-8 mb-8 shadow-sm">
            <h1 className="text-3xl font-bold text-[#0a0203] mb-4">Search Results</h1>
            <form onSubmit={handleSearch} className="flex gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search products, recipes, and articles..."
                  className="pl-12 py-6 text-lg"
                />
              </div>
              <Button type="submit" className="bg-[#003456] hover:bg-[#001d30] text-white px-8">
                Search
              </Button>
            </form>
            {query && (
              <p className="text-gray-500 mt-4">
                {loading ? 'Searching...' : `${totalResults} results for "${query}"`}
              </p>
            )}
          </div>

          {/* Tabs */}
          {query && (
            <div className="flex gap-2 mb-6 overflow-x-auto">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                    activeTab === tab.id
                      ? 'bg-[#003456] text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}

          {/* Results */}
          {loading ? (
            <div className="bg-white rounded-2xl p-16 text-center">
              <div className="animate-spin w-12 h-12 border-4 border-[#003456] border-t-transparent rounded-full mx-auto"></div>
              <p className="text-gray-500 mt-4">Searching...</p>
            </div>
          ) : query ? (
            <div className="space-y-8">
              {/* Products */}
              {(activeTab === 'all' || activeTab === 'products') && results.products.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Package className="h-5 w-5 text-[#003456]" />
                    <h2 className="text-xl font-bold text-[#0a0203]">Products</h2>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    {results.products.slice(0, activeTab === 'products' ? 20 : 3).map(product => (
                      <Link key={product.id} to={`/products/${product.id}`} className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow group">
                        <div className="aspect-square bg-gray-100 overflow-hidden relative">
                          <img src={product.image_url} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                          {product.on_sale && (
                            <span className="absolute top-3 left-3 bg-red-500 text-white px-2 py-1 rounded-full text-xs font-bold">SALE</span>
                          )}
                        </div>
                        <div className="p-4">
                          <h3 className="font-semibold text-[#0a0203] line-clamp-1">{product.name}</h3>
                          <p className="text-[#003456] font-bold mt-1">{formatPrice(product.on_sale && product.sale_price ? product.sale_price : product.price)}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                  {activeTab === 'all' && results.products.length > 3 && (
                    <button onClick={() => setActiveTab('products')} className="text-[#003456] font-medium mt-4 flex items-center gap-1 hover:underline">
                      View all {results.products.length} products <ArrowRight className="h-4 w-4" />
                    </button>
                  )}
                </div>
              )}

              {/* Recipes */}
              {(activeTab === 'all' || activeTab === 'recipes') && results.recipes.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <ChefHat className="h-5 w-5 text-[#003456]" />
                    <h2 className="text-xl font-bold text-[#0a0203]">Recipes</h2>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    {results.recipes.slice(0, activeTab === 'recipes' ? 20 : 3).map(recipe => (
                      <Link key={recipe.id} to={`/recipes/${recipe.slug}`} className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow group">
                        <div className="h-40 bg-gray-100 overflow-hidden">
                          <img src={recipe.image_url || 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400'} alt={recipe.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                        </div>
                        <div className="p-4">
                          <h3 className="font-semibold text-[#0a0203] line-clamp-1">{recipe.title}</h3>
                          {recipe.prep_time && (
                            <div className="flex items-center gap-1 text-gray-500 text-sm mt-1">
                              <Clock className="h-3 w-3" />
                              {recipe.prep_time} mins
                            </div>
                          )}
                        </div>
                      </Link>
                    ))}
                  </div>
                  {activeTab === 'all' && results.recipes.length > 3 && (
                    <button onClick={() => setActiveTab('recipes')} className="text-[#003456] font-medium mt-4 flex items-center gap-1 hover:underline">
                      View all {results.recipes.length} recipes <ArrowRight className="h-4 w-4" />
                    </button>
                  )}
                </div>
              )}

              {/* Blog */}
              {(activeTab === 'all' || activeTab === 'blog') && results.blogs.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <FileText className="h-5 w-5 text-[#003456]" />
                    <h2 className="text-xl font-bold text-[#0a0203]">Blog Articles</h2>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    {results.blogs.slice(0, activeTab === 'blog' ? 20 : 3).map(post => (
                      <Link key={post.id} to={`/blog/${post.slug}`} className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow group">
                        {post.featured_image && (
                          <div className="h-40 bg-gray-100 overflow-hidden">
                            <img src={post.featured_image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                          </div>
                        )}
                        <div className="p-4">
                          <h3 className="font-semibold text-[#0a0203] line-clamp-2">{post.title}</h3>
                          {post.excerpt && <p className="text-gray-500 text-sm mt-1 line-clamp-2">{stripHtml(post.excerpt, 200)}</p>}
                        </div>
                      </Link>
                    ))}
                  </div>
                  {activeTab === 'all' && results.blogs.length > 3 && (
                    <button onClick={() => setActiveTab('blog')} className="text-[#003456] font-medium mt-4 flex items-center gap-1 hover:underline">
                      View all {results.blogs.length} articles <ArrowRight className="h-4 w-4" />
                    </button>
                  )}
                </div>
              )}

              {/* No Results */}
              {totalResults === 0 && (
                <div className="bg-white rounded-2xl p-16 text-center">
                  <Search className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-[#0a0203] mb-2">No results found</h3>
                  <p className="text-gray-500 mb-6">We couldn't find anything matching "{query}"</p>
                  <div className="flex flex-wrap justify-center gap-4">
                    <Link to="/products">
                      <Button variant="outline" className="border-[#003456] text-[#003456]">Browse Products</Button>
                    </Link>
                    <Link to="/recipes">
                      <Button variant="outline" className="border-[#003456] text-[#003456]">View Recipes</Button>
                    </Link>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-2xl p-16 text-center">
              <Search className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-[#0a0203] mb-2">What are you looking for?</h3>
              <p className="text-gray-500">Search for products, recipes, or blog articles</p>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SearchResults;
